/**
 * Auth Guard для защиты маршрутов
 * Аналог lib/app/router/role_guard.dart (CrmGuard)
 */
import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '../stores/auth.store'

/**
 * Проверка аутентификации перед переходом на защищенные маршруты.
 * RBAC-логика (блокировка маршрутов для MARKETER) — в глобальном beforeEach (router/index.ts).
 */
export const authGuard = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
) => {
  const authStore = useAuthStore()

  // Public routes (no auth required)
  const publicRoutes = ['/login', '/']

  if (publicRoutes.includes(to.path)) {
    next()
    return
  }

  // Check if authenticated
  if (!authStore.isAuthenticated) {
    next({
      path: '/login',
      query: { redirect: to.fullPath },
    })
    return
  }

  // Allow access
  next()
}

/**
 * Redirect authenticated users away from login page
 */
export const guestGuard = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
) => {
  const authStore = useAuthStore()

  if (authStore.isAuthenticated) {
    // Already logged in, redirect to dashboard
    next('/dashboard')
    return
  }

  next()
}

/** Проверка роли AGENT (без учёта регистра). */
const hasAgentRole = (roles?: string[]): boolean =>
  !!roles?.some((r) => r.toLowerCase() === 'agent')

/**
 * Гард страниц для РОПа и администратора (DEV-197: аналитика менеджеров).
 * Остальные роли уводим на дашборд; неаутентифицированных — на логин.
 */
export const hosOrAdminGuard = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
) => {
  const authStore = useAuthStore()

  if (!authStore.isAuthenticated) {
    next({ path: '/login', query: { redirect: to.fullPath } })
    return
  }

  const roles = (authStore.user?.roles ?? []).map((r) => r.toLowerCase())
  if (!roles.includes('hos') && !roles.includes('admin')) {
    next('/dashboard')
    return
  }

  next()
}

/**
 * Гард страниц только для администратора (DEV-352: маршрутизация обращений).
 * Остальные роли уводим на дашборд; неаутентифицированных — на логин.
 */
export const adminGuard = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
) => {
  const authStore = useAuthStore()

  if (!authStore.isAuthenticated) {
    next({ path: '/login', query: { redirect: to.fullPath } })
    return
  }

  const roles = (authStore.user?.roles ?? []).map((r) => r.toLowerCase())
  if (!roles.includes('admin')) {
    next('/dashboard')
    return
  }

  next()
}

/**
 * Гард веб-инбокса обращений агентов (DEV-436): админ, РОП и менеджер.
 * Менеджер допускается (в отличие от hosOrAdminGuard) — «свои vs все обращения»
 * решает бэк по роли. Остальные роли уводим на дашборд; неаутентифицированных — на логин.
 */
export const agentSupportGuard = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
) => {
  const authStore = useAuthStore()

  if (!authStore.isAuthenticated) {
    next({ path: '/login', query: { redirect: to.fullPath } })
    return
  }

  const roles = (authStore.user?.roles ?? []).map((r) => r.toLowerCase())
  if (!roles.includes('hos') && !roles.includes('admin') && !roles.includes('manager')) {
    next('/dashboard')
    return
  }

  next()
}

/**
 * Гард защищённых маршрутов кабинета (/dashboard, /clients, /submit-client, …).
 * Требует аутентификации и роли AGENT. DEV-232.
 *
 * Не-агента гасим и уводим на /login, а НЕ на /dashboard: после выпила префикса
 * /agents (DEV-1014) кабинет живёт в корне и /dashboard закрыт этим же гвардом,
 * поэтому редирект «не-агент → /dashboard» указывал сам на себя. Роутер уходил в
 * бесконечную навигацию, в проде это роняло вкладку (в dev видно как «Detected a
 * possibly infinite redirection in a navigation guard»). Механика та же, что у
 * гейта партнёров в router/index.ts: живой токен в localStorage создаёт видимость
 * входа, поэтому сессию снимаем целиком. DEV-1486.
 */
export const agentGuard = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
) => {
  const authStore = useAuthStore()

  if (!authStore.isAuthenticated) {
    next({ path: '/login', query: { redirect: to.fullPath } })
    return
  }
  if (!hasAgentRole(authStore.user?.roles)) {
    await authStore.logout({ redirect: false })
    next({ path: '/login', query: { reason: 'not_agent' } })
    return
  }
  next()
}

/**
 * Гард публичных агентских страниц (login/register/forgot). Залогиненного агента
 * уводим в /home. DEV-232.
 * reset-password/confirm-email НЕ используют этот гард (доступны всем, в т.ч. по ссылке из письма).
 *
 * Залогиненного не-агента оставляем на форме: отправить его в /dashboard — вторая
 * половина петли, описанной в agentGuard (login → dashboard → login → …). DEV-1486.
 */
export const agentGuestGuard = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
) => {
  const authStore = useAuthStore()

  if (authStore.isAuthenticated && hasAgentRole(authStore.user?.roles)) {
    next('/home')
    return
  }
  next()
}
